import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { preloadImages, getImageDimensions, groupByProductId } from "./exportImageUtils";
import { getCurrencySymbol } from "./currency";

const COMPANY = {
  name: "Better Home Fashion Inc",
  address: "280 Bowes Rd, Concord, ON L4K 1J9",
  contact: "Tel: 905-660-8741   Fax: 416-352-5796",
};

// iter 186 (2026-02): PDF exporter is now column-driven, matching the Excel
// exporter's model. Field IDs, order, labels and widths must line up with
// FIELD_META in exportQuotationExcel.js so what the dialog toggles produces
// the same-shaped Excel and PDF.
const FIELD_META = {
  photo:       { label: "Photo",       width: 76.5, level: "product", isPhoto: true },
  description: { label: "Description", width: 95,   level: "product", valueFn: (r) => r.description || "-" },
  material:    { label: "Material",    width: 80,   level: "product", valueFn: (r) => r.specification || "-" },
  item_no:     { label: "Item No",     width: 54,   level: "variant", valueFn: (r) => ({ content: r.item_no || "-", styles: { fontStyle: "bold" } }) },
  size:        { label: "Size",        width: 36,   level: "variant", valueFn: (r) => r.size || "-" },
  pcs_per_ctn: { label: "Pcs/CTN",     width: 28,   level: "variant", valueFn: (r) => r.pcs_per_ctn ?? "-", align: "right" },
  // iter 189: dimensional fields mirror the Excel exporter's meta.
  carton_dims: { label: "Carton L\u00d7W\u00d7H (cm)", width: 62, level: "variant",
                 valueFn: (r) => (r.carton_length && r.carton_width && r.carton_height
                                   ? `${r.carton_length}\u00d7${r.carton_width}\u00d7${r.carton_height}`
                                   : "-") },
  cbm:         { label: "CBM/CTN",     width: 40,   level: "variant",
                 valueFn: (r) => (r.cbm != null ? Number(r.cbm).toFixed(4) : "-"), align: "right" },
  qty_40ft:    { label: "Qty/40'HQ",   width: 44,   level: "variant",
                 valueFn: (r) => (r.qty_40ft != null ? Number(r.qty_40ft).toLocaleString() : "-"), align: "right" },
  // iter 278: QTY = available to this customer; Reserved = held for this quotation.
  qty:         { label: "QTY",         width: 40,   level: "variant", valueFn: (r) => r.ci_available ?? r.ci_inventory_qty ?? "-", align: "right" },
  reserved_qty: { label: "Reserved",   width: 40,   level: "variant", valueFn: (r) => (r.ci_reserved_qty ? String(r.ci_reserved_qty) : "-"), align: "right" },
  // iter 221 (2026-02): 4 个可选新字段,变体级。宽度按 PDF 紧凑布局微调。
  gt_sku:         { label: "GT SKU",           width: 50, level: "variant", valueFn: (r) => r.gt_sku || "-" },
  order_qty:      { label: "Order QTY",        width: 44, level: "variant",
                    valueFn: (r) => (r.qty != null ? Number(r.qty).toLocaleString() : "-"), align: "right" },
  purchase_price: { label: "Purchase (RMB)",   width: 54, level: "variant",
                    valueFn: (r) => (r.purchase_price != null ? Number(r.purchase_price).toFixed(2) : "-"), align: "right" },
  gt_price:       { label: "GT Price",         width: 44, level: "variant",
                    valueFn: (r) => (r.gt_price != null ? Number(r.gt_price).toFixed(2) : "-"), align: "right" },
  barcode:     { label: "Barcode",     width: 55,   level: "variant", valueFn: (r) => r.upc || "-" },
  price:       { label: "Price",       width: 50,   level: "variant", isPrice: true, align: "right" },
};

function drawCompanyHeader(doc) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(COMPANY.name, 40, 36);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(COMPANY.address, 40, 50);
  doc.text(COMPANY.contact, 40, 62);
}

export async function exportQuotationPdf(rows, filename = "quotation", currency = "USD", fields) {
  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  drawCompanyHeader(doc);
  const symbol = getCurrencySymbol(currency);

  const orderedIds = Object.keys(FIELD_META);
  const activeIds = fields
    ? orderedIds.filter((id) => fields.includes(id))
    : orderedIds;
  const cols = activeIds.map((id) => ({ id, ...FIELD_META[id] }));
  const photoIdx = activeIds.indexOf("photo");

  const imageMap = photoIdx >= 0
    ? await preloadImages(rows.map((r) => r.photo_url))
    : new Map();
  const dimensionsMap = new Map(
    await Promise.all(
      [...imageMap.entries()].map(async ([url, dataUrl]) => [url, dataUrl ? await getImageDimensions(dataUrl) : null]),
    ),
  );
  const groupSize = groupByProductId(rows);

  const body = rows.map((r, idx) => {
    const isGroupStart = groupSize[idx] > 0;
    const buildCell = (c) => {
      if (c.isPrice) {
        return { content: r.price != null ? `${symbol}${Number(r.price).toFixed(2)}` : "-", styles: { fontStyle: "bold" } };
      }
      return c.valueFn(r);
    };

    if (!isGroupStart) {
      return cols.filter((c) => c.level === "variant").map(buildCell);
    }
    // On a group start, emit product-level cells (with rowSpan) followed by variant cells.
    const productCells = cols
      .filter((c) => c.level === "product")
      .map((c) => (c.isPhoto
        ? { content: "", rowSpan: groupSize[idx] }
        : { content: c.valueFn(r), rowSpan: groupSize[idx] }));
    const variantCells = cols.filter((c) => c.level === "variant").map(buildCell);
    return [...productCells, ...variantCells];
  });

  const columnStyles = {};
  cols.forEach((c, i) => {
    columnStyles[i] = { cellWidth: c.width, ...(c.align ? { halign: c.align } : {}) };
  });

  autoTable(doc, {
    startY: 84,
    head: [cols.map((c) => c.label)],
    body,
    styles: { fontSize: 8, cellPadding: 4, lineColor: [200, 200, 200], lineWidth: 0.5, minCellHeight: 76.5 },
    headStyles: { fillColor: [37, 99, 235], textColor: [255, 255, 255], fontStyle: "bold" },
    columnStyles,
    didDrawCell: (data) => {
      if (photoIdx < 0) return;
      if (data.section === "body" && data.column.index === photoIdx) {
        const url = rows[data.row.index]?.photo_url;
        const dataUrl = imageMap.get(url);
        if (dataUrl) {
          try {
            const box = Math.min(data.cell.width, data.cell.height) - 6;
            const dims = dimensionsMap.get(url);
            const scale = dims ? Math.min(box / dims.width, box / dims.height) : 1;
            const width = dims ? dims.width * scale : box;
            const height = dims ? dims.height * scale : box;
            const x = data.cell.x + (data.cell.width - width) / 2;
            const y = data.cell.y + (data.cell.height - height) / 2;
            doc.addImage(dataUrl, x, y, width, height);
          } catch {
            // ignore malformed image
          }
        }
      }
    },
  });

  doc.save(`${filename}-${new Date().toISOString().slice(0, 10)}.pdf`);
}
